import { ChangeDetectionStrategy, Component, inject, input, output } from '@angular/core';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';

export interface ConfirmationDialogData {
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'default' | 'danger';
}

@Component({
  selector: 'app-confirmation-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="dialog-container" role="alertdialog" aria-modal="true">
      <h2 class="dialog-title" id="dialog-title">{{ data.title }}</h2>
      <p class="dialog-message" id="dialog-message">{{ data.message }}</p>
      <div class="dialog-actions">
        <button
          type="button"
          class="btn btn-secondary"
          (click)="onCancel()">
          {{ data.cancelLabel || 'Cancel' }}
        </button>
        <button
          type="button"
          [class]="'btn ' + (data.variant === 'danger' ? 'btn-danger' : 'btn-primary')"
          (click)="onConfirm()">
          {{ data.confirmLabel || 'Confirm' }}
        </button>
      </div>
    </div>
  `,
  styles: `
    .dialog-container {
      background: white;
      border-radius: 0.75rem;
      padding: 1.5rem;
      max-width: 28rem;
      box-shadow: 0 20px 25px -5px rgb(0 0 0 / 0.1);
    }

    .dialog-title {
      margin: 0;
      font-size: 1.125rem;
      font-weight: 600;
      color: oklch(0.2 0 0);
    }

    .dialog-message {
      margin: 0.75rem 0 0;
      font-size: 0.875rem;
      color: oklch(0.45 0 0);
      line-height: 1.5;
    }

    .dialog-actions {
      display: flex;
      justify-content: flex-end;
      gap: 0.75rem;
      margin-top: 1.5rem;
    }

    .btn {
      padding: 0.5rem 1rem;
      border-radius: 0.5rem;
      font-size: 0.875rem;
      font-weight: 500;
      cursor: pointer;
      border: none;
      transition: background-color 0.15s;
    }

    .btn-secondary {
      background: oklch(0.95 0 0);
      color: oklch(0.3 0 0);
    }

    .btn-secondary:hover {
      background: oklch(0.9 0 0);
    }

    .btn-primary {
      background: oklch(0.5 0.2 250);
      color: white;
    }

    .btn-primary:hover {
      background: oklch(0.45 0.2 250);
    }

    .btn-danger {
      background: oklch(0.55 0.2 25);
      color: white;
    }

    .btn-danger:hover {
      background: oklch(0.5 0.2 25);
    }

    .btn:focus-visible {
      outline: 2px solid oklch(0.5 0.2 250);
      outline-offset: 2px;
    }
  `,
})
export class ConfirmationDialogComponent {
  protected readonly dialogRef = inject(DialogRef<boolean>);
  protected readonly data = inject<ConfirmationDialogData>(DIALOG_DATA);

  protected onConfirm(): void {
    this.dialogRef.close(true);
  }

  protected onCancel(): void {
    this.dialogRef.close(false);
  }
}
